import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ExtensionRounded from '@mui/icons-material/ExtensionRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import HowToRegRounded from '@mui/icons-material/HowToRegRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import RateReviewRounded from '@mui/icons-material/RateReviewRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import { StatTile } from '@/components/data/StatTile';
import { SectionTabs } from '@/components/SectionTabs';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useConfirm } from '@/components/feedback/confirmContext';
import { useAsync } from '@/hooks/useAsync';
import { moduleIcon } from '@/modules/registry';
import { downloadFile, getAudit, getOverview, toggleModule } from '@/platform/api';
import { useSession } from '@/platform/session';
import type { ModuleInfo } from '@/platform/types';
import { formatDateTime, formatRelative } from '@/utils/format';
import { PageHeader } from '../PageHeader';

export function AdminTabs() {
  const { me } = useSession();
  return (
    <SectionTabs
      label="Seções da administração"
      items={[
        { to: '/admin', label: 'Módulos', end: true, icon: <ExtensionRounded fontSize="small" /> },
        {
          to: '/admin/approvals',
          label: 'Aprovações',
          badge: me.pendingApprovals,
          icon: <HowToRegRounded fontSize="small" />,
        },
        {
          to: '/admin/institutions',
          label: 'Instituições e cursos',
          icon: <AccountBalanceRounded fontSize="small" />,
        },
        {
          to: '/admin/billing',
          label: 'Assinaturas',
          icon: <CreditCardRounded fontSize="small" />,
        },
        {
          to: '/admin/revisores',
          label: 'Revisores',
          icon: <RateReviewRounded fontSize="small" />,
        },
        { to: '/admin/enem', label: 'ENEM', icon: <SchoolRounded fontSize="small" /> },
        { to: '/admin/activity', label: 'Atividade', icon: <HistoryRounded fontSize="small" /> },
      ]}
    />
  );
}

/** Modules on/off switchboard + system overview. */
export function AdminPage() {
  const { allModules, refresh } = useSession();
  const overview = useAsync(getOverview, [allModules]);
  const notify = useNotify();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (module: ModuleInfo) => {
    const enabling = !module.enabled;
    if (!enabling) {
      const ok = await confirm({
        title: `Desativar ${module.name}?`,
        description:
          'O módulo some para todas as pessoas (inclusive quem estiver no meio de uma atividade). Nenhum dado é apagado: ao reativar, tudo volta como estava.',
        confirmLabel: 'Desativar',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(module.key);
    try {
      await toggleModule(module.key, enabling);
      await refresh();
      notify(`${module.name} ${enabling ? 'ativado' : 'desativado'}.`, 'success');
    } catch {
      notify('Não foi possível alterar o módulo.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const o = overview.data;
  return (
    <>
      <PageHeader
        title="Administração"
        subtitle="Módulos da plataforma, pessoas e atividade."
        actions={
          <Button
            variant="outlined"
            startIcon={<DownloadRounded />}
            onClick={() =>
              void downloadFile('/admin/consents.csv', 'comunicacoes.csv').catch(() =>
                notify('Não foi possível baixar a lista.', 'error'),
              )
            }
          >
            Quem aceitou comunicações
          </Button>
        }
      />
      <AdminTabs />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Estudantes"
            value={o?.students ?? 0}
            icon={SchoolRounded}
            loading={overview.loading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Professores e coordenação"
            value={(o?.teachers ?? 0) + (o?.coordinators ?? 0) + (o?.admins ?? 0)}
            icon={AdminPanelSettingsRounded}
            loading={overview.loading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Turmas"
            value={o?.groups ?? 0}
            icon={GroupsRounded}
            loading={overview.loading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Acessos (7 dias)"
            value={o?.activeLast7Days ?? 0}
            icon={PersonRounded}
            loading={overview.loading}
          />
        </Grid>
      </Grid>

      <Stack
        direction="row"
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}
      >
        <Typography variant="h6" component="h2">
          Módulos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {allModules.filter((m) => m.enabled).length} de {allModules.length} ativos
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {allModules.map((module) => {
          const Icon = moduleIcon(module.key);
          return (
            <Grid key={module.key} size={{ xs: 12, md: 6 }}>
              <Card
                sx={(t) => ({
                  height: '100%',
                  borderColor: module.enabled ? t.vars.palette.primary.main : undefined,
                  borderWidth: module.enabled ? 1.5 : 1,
                })}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                    <Box
                      sx={(t) => ({
                        width: 48,
                        height: 48,
                        borderRadius: 3.5,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: module.enabled
                          ? t.vars.palette.primary.main
                          : t.vars.palette.background.subtle,
                        color: module.enabled
                          ? t.vars.palette.primary.contrastText
                          : t.vars.palette.text.secondary,
                        transition: 'all 250ms',
                      })}
                    >
                      <Icon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="h6" component="h3">
                          {module.name}
                        </Typography>
                        <Chip
                          size="small"
                          variant="soft"
                          color={module.enabled ? 'lime' : 'default'}
                          label={module.enabled ? 'Ativo' : 'Desativado'}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        v{module.version}
                        {module.updatedAt && ` · alterado ${formatRelative(module.updatedAt)}`}
                      </Typography>
                    </Box>
                    <Switch
                      checked={module.enabled}
                      disabled={busy === module.key}
                      onChange={() => void toggle(module)}
                      slotProps={{
                        input: {
                          'aria-label': `${module.enabled ? 'Desativar' : 'Ativar'} ${module.name}`,
                        },
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {module.description}
                  </Typography>
                  {module.features.length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                      {module.features.map((f) => (
                        <Stack
                          key={f}
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'flex-start' }}
                        >
                          <CheckRounded sx={{ fontSize: 16, mt: 0.25, color: 'success.main' }} />
                          <Typography variant="body2">{f}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={(t) => ({
              height: '100%',
              borderStyle: 'dashed',
              bgcolor: 'transparent',
              borderColor: t.vars.palette.divider,
            })}
          >
            <CardContent
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                gap: 1,
                py: 4,
              }}
            >
              <ExtensionRounded color="disabled" sx={{ fontSize: 36 }} />
              <Typography variant="subtitle1">Próximos módulos</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                Cursos e aulas, fórum, frequência, calendário, notas consolidadas… Cada módulo novo
                aparece aqui para ser ativado.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {o && (
        <Alert severity="info" icon={<StorageRounded />} sx={{ mt: 3 }}>
          Banco de dados: <b>{o.database}</b> · versão da plataforma {o.version}
        </Alert>
      )}
    </>
  );
}

export function ActivityPage() {
  const audit = useAsync((signal) => getAudit(100, signal));
  return (
    <>
      <PageHeader title="Administração" subtitle="Registro das ações importantes na plataforma." />
      <AdminTabs />
      {audit.error ? (
        <Alert severity="error">Não foi possível carregar a atividade.</Alert>
      ) : (
        <Card>
          {audit.loading
            ? [0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={40} sx={{ m: 2 }} />
              ))
            : (audit.data ?? []).map((entry) => (
                <Box
                  key={entry.id}
                  sx={(t) => ({
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 200px 140px' },
                    gap: { xs: 0.25, sm: 2 },
                    px: 2.5,
                    py: 1.5,
                    '& + &': { borderTop: `1px solid ${t.vars.palette.divider}` },
                  })}
                >
                  <Typography variant="body2">{entry.summary}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {entry.actorName ?? entry.actorEmail ?? 'Sistema'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    title={formatDateTime(entry.createdAt)}
                    sx={{ textAlign: { sm: 'right' } }}
                  >
                    {formatRelative(entry.createdAt)}
                  </Typography>
                </Box>
              ))}
          {audit.data?.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              Nenhuma atividade registrada.
            </Typography>
          )}
        </Card>
      )}
    </>
  );
}
